import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type {
  MailrailDripEmailCampaign,
  TAutosendSubtype,
  TEmailCampaignMailing,
} from '@feathr/blackbox';
import { MailrailDripStep } from '@feathr/blackbox';
import { CardV2 as Card, EmailTemplateSelect, Label, Well } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import MailrailTriggerSummary from '../../../CampaignSummary/MailrailTriggerSummary';
import useDripText from '../../DripCampaignEdit/useDripText';
import { bestUnit, getUnitLabel, toDisplayValue } from '../timeUnits.utils';

import * as styles from './DripStepCardPreview.css';

interface IDripStepCardPreviewProps {
  campaign: MailrailDripEmailCampaign;
  /**
   * The mailing this card summarizes: a {@link MailrailDripStep} for a step card (1..N), or the
   * campaign itself for the enrollment card (card 0).
   */
  mailing: TEmailCampaignMailing;
}

/**
 * The collapsed, read-only summary of a drip Builder card. It works for both a step (cards 1..N)
 * and the enrollment card (card 0 = the campaign).
 *
 * @param props - {@link IDripStepCardPreviewProps}.
 */
function DripStepCardPreview({
  campaign,
  mailing,
}: Readonly<IDripStepCardPreviewProps>): JSX.Element {
  const { t } = useTranslation();
  const { Templates } = useStore();
  const {
    builder: { targetingMessage, targetingDelayLabel, targetingDelayTooltip },
  } = useDripText();

  const step = mailing instanceof MailrailDripStep ? mailing : undefined;
  const isEnrollment = !step;

  function getStepTriggerSummary(): string {
    const triggerAction = step?.get('trigger_action');
    if (!triggerAction) {
      return t('No trigger selected');
    }
    return targetingMessage[triggerAction];
  }

  function getTriggerTypeLabel(): string {
    const subtype = campaign.get('subtype');
    const subtypeLabels: Record<TAutosendSubtype, string> = {
      activity: t('Activity happened'),
      update: t('Field changed'),
      time: t('Date triggered'),
    };
    return subtype ? subtypeLabels[subtype] : t('Not set');
  }

  function getDelaySummary(): string {
    // Read delay_seconds from whichever concrete model applies (the step for a step card, the
    // campaign for the enrollment card) — both declare it; `mailing` is the broad mailing type.
    const delaySeconds = (step ? step.get('delay_seconds') : campaign.get('delay_seconds')) ?? 0;
    if (!delaySeconds) {
      return t('Immediately');
    }
    const unit = bestUnit(delaySeconds);
    const count = toDisplayValue(delaySeconds, unit);
    // Compose the full label so the count drives the unit's plural form (not the wrapper key).
    const unitLabel = getUnitLabel(unit, count, t);
    return t('After {{count}} {{unitLabel}}', { count, unitLabel });
  }

  const templateId = mailing.get('template_id');
  const template = templateId ? Templates.get(templateId) : undefined;
  // Draft/Scheduled campaigns haven't published yet, so the email isn't rendered server-side —
  // show the template thumbnail. Published+ campaigns (Published/Stopped/Complete) get the
  // server-rendered preview.
  const isPrePublish = campaign.isDraft || campaign.isScheduled;
  const triggerAction = step?.get('trigger_action');

  return (
    <>
      <Card.Content>
        <Label weight={'medium'}>{isEnrollment ? t('Initial enrollment') : t('Automation')}</Label>
        <div className={styles.wells}>
          {isEnrollment ? (
            <>
              <Well layout={'vertical'} theme={'white'}>
                <Well.Row description={getTriggerTypeLabel()} label={t('Trigger type')} />
              </Well>
              <Well layout={'vertical'} theme={'white'}>
                <Well.Row
                  description={<MailrailTriggerSummary campaign={campaign} />}
                  label={t('Trigger')}
                />
              </Well>
              <Well layout={'vertical'} theme={'white'}>
                <Well.Row description={getDelaySummary()} label={t('Delay')} />
              </Well>
            </>
          ) : (
            <Well layout={'vertical'} theme={'white'}>
              <Well.Row
                description={getStepTriggerSummary()}
                label={t('Step targeting')}
                tooltip={t('Select who should be sent the email in this step')}
              />
              <Well.Row
                description={getDelaySummary()}
                label={triggerAction ? targetingDelayLabel[triggerAction] : t('Not set')}
                tooltip={triggerAction ? targetingDelayTooltip[triggerAction] : undefined}
              />
            </Well>
          )}
        </div>
      </Card.Content>
      <Card.Content>
        <Label weight={'medium'}>{t('Email')}</Label>
        <EmailTemplateSelect
          description={template?.get('preview_text')}
          imageUrl={isPrePublish && template ? template.get('thumbnail_url') : undefined}
          isLoading={template?.isPending}
          previewUrl={
            template && !isPrePublish
              ? campaign.getTemplatePreviewUrl({ mailingId: step?.get('step_id'), templateId })
              : undefined
          }
          readOnly={true}
          subtitle={template?.get('subject')}
          title={template ? mailing.get('from_address') ?? '' : t('No template selected')}
        />
      </Card.Content>
    </>
  );
}

export default observer(DripStepCardPreview);
